import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LinearProgress, RadioGroup } from '@mui/material';
import { isEmpty, isEqual, isNil } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { FIRM_TYPE } from 'common/constants/pageTypes';
import { useStore } from 'common/store';
import { MeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import {
  BULK_EXPORT_APP_BAR_ACTION_LABEL,
  BULK_EXPORT_APP_BAR_SUBTITLE,
  BULK_EXPORT_APP_BAR_TITLE,
  FIRM_EXPORT_OPTIONS,
} from 'layouts/Main/components/ExcelExportDialogV2/constants';
import { FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG } from 'pages/Firms/common/constants/firmSummary';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { useHasFirmSummary } from 'services/hooks/firm';
import { Logger } from 'utilities';
import BulkActionsContentWrapper from '../../BulkActionsLedger/components/BulkActionsContentWrapper/BulkActionsContentWrapper';
import { useBaseExcelExportForm, useBaseExcelExportState, useExcelExportLogic } from '../BaseExcelExportDialog';
import { BaseDialogProps, ExcelExportContextType } from '../types';
import { AutoUpdatedFirmZip, DateSelectorContainer, RadioOption, ReportStatusesDisplay } from './components';
import { ExportOption, FirmCurrentSelectedValues, FirmExcelExportDialogErrors } from './FirmExcelExportDialog.types';
import {
  useAutoUpdatedFirmZip,
  useCompanyReportsStatuses,
  useEnqueueIndividualCompanyReports,
  useReportsZipJob,
} from './hooks';
import SelectFundMeasurementDate from '../../BulkActionsLedger/components/SelectFundMeasurementDate/SelectFundMeasurementDate';

// Constants
const INFO_TOAST_DURATION_MS = 5000;

export const FirmExcelExportDialog = ({ open, onClose }: BaseDialogProps) => {
  const [{ firmInfo, fundList }] = useStore() as unknown as UseStoreValues;

  const {
    isLoading: isExportLoading,
    setCurrentSelectedValues,
    setFundsByMeasurementDate,
  } = useContext(ExcelExportContext) as unknown as ExcelExportContextType;

  const {
    selectedMeasurementDate: selectedDateInFirmLayout,
    mostRecentMeasurementDate, // this is a concrete measurement date serialization
    isLoadingMeasurementDate,
    companyMeasurementDates, // includes the virtual one ("Most Recent")
  } = useFirmMeasurementDateFilter();

  const [selectedOption, setSelectedOption] = useState<ExportOption>(FIRM_EXPORT_OPTIONS.FIRM_SUMMARY);
  const [selectedReportDate, setSelectedReportDate] = useState<MeasurementDate | null>(null);
  const [companySearchString, setCompanySearchString] = useState<string>('');
  const [hasFirmSummary, setHasFirmSummary] = useState(false);
  const [hasFundSummaries, setHasFundSummaries] = useState(false);

  const [errors, setErrors] = useState<FirmExcelExportDialogErrors>({} as FirmExcelExportDialogErrors);

  const hasFirmSummaryHook = useHasFirmSummary();

  const { formValues, updateFormValues } = useBaseExcelExportForm();
  const { isLoading, setIsLoading, setDataLoadError, resetState } = useBaseExcelExportState();

  const firmId = firmInfo?.id ?? null;

  // this controls data retrieval when "Most Recent" is selected and display of invalid state
  const isVirtualRecentDateSelectedForReport = useMemo<boolean>(
    () => selectedReportDate?.date === FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG,
    [selectedReportDate]
  );

  useEffect(() => {
    if (selectedDateInFirmLayout) {
      setSelectedReportDate(selectedDateInFirmLayout);
    }
  }, [selectedDateInFirmLayout]);

  const {
    handleGlobalToggle,
    handleIndividualChange,
    reportStatuses,
    reportStatusesMap,
    reportsToRequest,
    setStatusPollingInterval: setCompanyReportStatusPollingInterval,
    handleEnqueueCheckboxChange,
    enqueueToTrigger,
    setEnqueueToTrigger,
  } = useCompanyReportsStatuses(
    firmId,
    selectedReportDate,
    open && selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES
  );

  const { generateFirmSummaryReport } = useExcelExportLogic({
    pageType: FIRM_TYPE,
    firmInfo,
    companyInfo: null,
    fundInfo: null,
    currentMeasurementDateId: isVirtualRecentDateSelectedForReport ? null : selectedReportDate?.id,
    formValues,
    setErrors,
  });

  const { startZipJob, isPollingZipJob } = useReportsZipJob(firmId!);

  useEffect(() => {
    updateFormValues({
      export_firm_summary: Boolean(hasFirmSummary),
      export_firm_funds_summaries: Boolean(hasFundSummaries),
      export_not_formulated: false,
      report_date:
        selectedReportDate?.date === FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG ? null : selectedReportDate?.date,
    });
  }, [hasFirmSummary, hasFundSummaries, updateFormValues, selectedReportDate]);

  const { enqueueButtonHandler, enqueueButtonHandlerIsDisabled } = useEnqueueIndividualCompanyReports(
    enqueueToTrigger,
    firmId,
    selectedReportDate,
    setEnqueueToTrigger,
    !isVirtualRecentDateSelectedForReport
  );

  const { autoUpdatedZipStatus } = useAutoUpdatedFirmZip({
    selectedReportDate,
    isVirtualRecentDateSelectedForReport,
    firmId,
    componentIsVisible: open && selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES,
  });

  const handleExport = useCallback(async () => {
    if (selectedOption === FIRM_EXPORT_OPTIONS.FIRM_SUMMARY) {
      await generateFirmSummaryReport();
    } else if (selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES) {
      // read keys where the value is true
      const company_measurement_dates_ids = Object.entries(reportsToRequest)
        .filter(([_, toRequest]) => toRequest)
        .map(([id, _]) => Number(id));
      await startZipJob(company_measurement_dates_ids);
      setCompanyReportStatusPollingInterval(null); // to stop polling individual statuses while zip job is ongoing
    }
  }, [selectedOption, generateFirmSummaryReport, reportsToRequest, startZipJob, setCompanyReportStatusPollingInterval]);

  const hasAtLeastOneReport = hasFirmSummary || hasFundSummaries;
  const isExportDisabled =
    isLoading ||
    isExportLoading ||
    !(isEmpty(errors) && hasAtLeastOneReport) ||
    (selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES &&
      Object.values(reportsToRequest || {}).every(value => value === false)) ||
    isPollingZipJob;

  useEffect(() => {
    if (!open) {
      setSelectedOption(FIRM_EXPORT_OPTIONS.FIRM_SUMMARY);
      setErrors({});
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    const loadFirmData = async () => {
      if (!open || !firmInfo?.id || !selectedReportDate || isNil(fundList)) {
        return;
      }

      setIsLoading(true);
      setDataLoadError(null);

      try {
        const fundsWithMeasurementDate =
          fundList
            ?.filter(fund => fund.id)
            .map(fund => ({
              fund: fund.id!,
              measurement_date: isVirtualRecentDateSelectedForReport
                ? mostRecentMeasurementDate?.md_id
                : selectedReportDate?.id,
            })) ?? [];

        // Convert to FundByMeasurementDate format and store in context
        const fundsByMeasurementDateForContext =
          fundList
            ?.filter(fund => fund.id)
            .map(fund => ({
              fund: {
                id: fund.id!,
                name: fund.name || '',
                slug: fund.slug || '',
                currency: fund.currency || '',
              },
              measurementDate: isVirtualRecentDateSelectedForReport ? mostRecentMeasurementDate : selectedReportDate,
            })) ?? [];

        setFundsByMeasurementDate(fundsByMeasurementDateForContext);

        const firmData = await hasFirmSummaryHook(firmInfo.id, fundsWithMeasurementDate);

        if (firmData) {
          const hasSummary = firmData.has_summary || false;
          setHasFirmSummary(hasSummary);
          setHasFundSummaries(hasSummary);

          // Show toast if no data is available for export
          if (!hasSummary) {
            enqueueSnackbar('No data available to export', {
              variant: 'info',
              autoHideDuration: INFO_TOAST_DURATION_MS,
            });
          }
        } else {
          setHasFirmSummary(false);
          setHasFundSummaries(false);
          // Show toast if API call failed or returned no data
          enqueueSnackbar('No data available to export', {
            variant: 'info',
            autoHideDuration: INFO_TOAST_DURATION_MS,
          });
        }
        const contextValues: Partial<FirmCurrentSelectedValues> = {};
        setCurrentSelectedValues(contextValues);
      } catch (error) {
        Logger.error('Failed to load firm data:', error);
        setDataLoadError('Failed to load firm data');
      } finally {
        setIsLoading(false);
      }
    };

    loadFirmData();
    // Disabling exhaustive deps to prevent infinite re-renders and unnecessary API calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    firmInfo?.id,
    selectedReportDate,
    isLoadingMeasurementDate,
    fundList,
    setFundsByMeasurementDate,
    isVirtualRecentDateSelectedForReport,
  ]);

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedOption(event.target.value as ExportOption);
    },
    [setSelectedOption]
  );
  const appBarProps = useMemo(
    () => ({
      onClose,
      onSave: handleExport,
      isButtonDisabled: isExportDisabled,
      title: BULK_EXPORT_APP_BAR_TITLE,
      subtitle: BULK_EXPORT_APP_BAR_SUBTITLE,
      actionLabel: BULK_EXPORT_APP_BAR_ACTION_LABEL,
    }),
    [onClose, handleExport, isExportDisabled]
  );

  const reportStatesAreSynchronized = isEqual(Object.keys(reportsToRequest), Object.keys(reportStatusesMap));

  return (
    <BulkActionsContentWrapper open={open} onClose={onClose} isLoading={isLoading} appBarProps={appBarProps}>
      {isPollingZipJob ? <LinearProgress /> : null}
      <DateSelectorContainer>
        {/* naming is a bit off here, but reusing existing component for consistency  */}
        <SelectFundMeasurementDate
          selectedFundMeasurementDateState={{
            value: selectedReportDate,
            setter: setSelectedReportDate,
          }}
          uniqueFundMeasurementDatesByDate={companyMeasurementDates}
          label="Measurement Date"
        />
      </DateSelectorContainer>
      <RadioGroup value={selectedOption} onChange={handleOptionChange} sx={{ marginLeft: '25px' }}>
        <RadioOption value={FIRM_EXPORT_OPTIONS.FIRM_SUMMARY} label="Firm Summary" />
        <RadioOption value={FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES} label="All Portfolio Company Valuations" />
      </RadioGroup>
      {!isVirtualRecentDateSelectedForReport && selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES ? (
        <AutoUpdatedFirmZip autoUpdatedZipStatus={autoUpdatedZipStatus} />
      ) : null}
      {selectedOption === FIRM_EXPORT_OPTIONS.PORTFOLIO_COMPANIES &&
      reportsToRequest &&
      reportStatuses &&
      reportStatesAreSynchronized ? (
          <ReportStatusesDisplay
            handleGlobalToggle={handleGlobalToggle}
            handleIndividualChange={handleIndividualChange}
            reportStatuses={reportStatuses}
            companySearchString={companySearchString}
            setCompanySearchString={setCompanySearchString}
            reportsToRequest={reportsToRequest}
            handleEnqueueCheckboxChange={handleEnqueueCheckboxChange}
            enqueueToTrigger={enqueueToTrigger}
            setEnqueueToTrigger={setEnqueueToTrigger}
            enqueueButtonHandler={enqueueButtonHandler}
            enqueueButtonHandlerIsDisabled={enqueueButtonHandlerIsDisabled}
            displayDateTooltip={isVirtualRecentDateSelectedForReport}
          />
        ) : null}
    </BulkActionsContentWrapper>
  );
};
